// src/components/AuthPage.js
import React, { useState, useContext, useEffect } from 'react'; // Added useEffect
import { AuthContext } from '../App';
import { db, auth } from '../firebaseConfig';
import { doc, setDoc } from 'firebase/firestore';
import { sendPasswordResetEmail } from 'firebase/auth';

// --- 0. Basic Styling (Copied from App.js for now) ---
const formStyles = {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    marginBottom: '20px',
    padding: '15px',
    border: '1px solid #eee',
    borderRadius: '5px',
    backgroundColor: '#f9f9f9',
};

const inputStyles = {
    padding: '10px',
    borderRadius: '4px',
    border: '1px solid #ccc',
};

const buttonStyles = {
    padding: '10px 15px',
    borderRadius: '4px',
    border: 'none',
    cursor: 'pointer',
    backgroundColor: '#007bff',
    color: 'white',
    fontSize: '1em',
};

const successButtonStyles = {
    ...buttonStyles,
    backgroundColor: '#28a745',
};

const textButtonStyles = {
    background: 'none',
    border: 'none',
    color: '#007bff',
    cursor: 'pointer',
    textAlign: 'left',
    padding: '0',
    fontSize: '0.9em',
    marginTop: '5px',
};

const messageStyles = {
    padding: '10px',
    borderRadius: '4px',
    margin: '10px 0',
};

const successMessageStyles = {
    ...messageStyles,
    backgroundColor: '#d4edda',
    color: '#155724',
};

const errorMessageStyles = {
    ...messageStyles,
    backgroundColor: '#f8d7da',
    color: '#721c24',
};

const passwordGuideStyles = {
    fontSize: '0.85em',
    color: '#555',
    marginBottom: '10px',
    paddingLeft: '10px',
    borderLeft: '2px solid #ddd',
};

const passwordIssueStyles = {
    color: '#dc3545',
    fontWeight: 'bold',
};

const passwordOkStyles = {
    color: '#28a745',
};

// --- Helper for Password Validation ---
const validatePasswordClientSide = (password, email, fullName, confirmPassword) => { // Added confirmPassword
    const errors = [];
    const minLength = 6;

    if (password.length < minLength) {
        errors.push(`Password must be at least ${minLength} characters long.`);
    }
    if (!/[A-Z]/.test(password)) {
        errors.push("Password must contain at least one uppercase letter.");
    }
    if (!/[a-z]/.test(password)) {
        errors.push("Password must contain at least one lowercase letter.");
    }
    if (!/[0-9]/.test(password)) {
        errors.push("Password must contain at least one number.");
    }
    if (!/[^A-Za-z0-9]/.test(password)) {
        errors.push("Password must contain at least one special character (e.g., !@#$%^&*).");
    }

    const emailParts = email.split('@')[0].split(/[._-]/);
    const nameParts = fullName ? fullName.split(' ') : [];
    const forbiddenParts = [...emailParts, ...nameParts].filter(part => part.length > 2);

    if (forbiddenParts.some(part => password.toLowerCase().includes(part.toLowerCase()))) {
        errors.push("Password should not contain parts of your email or full name.");
    }

    // New check for password confirmation
    if (confirmPassword !== undefined && password !== confirmPassword) { // Only check if confirmPassword is provided
        errors.push("Passwords do not match.");
    }

    return errors;
};


// --- Login/Register Component ---
export default function AuthPage() {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState(''); // New state for confirm password
    const [fullName, setFullName] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [department, setDepartment] = useState('');
    const [position, setPosition] = useState('');

    const [message, setMessage] = useState('');
    const [isSuccess, setIsSuccess] = useState(false);
    const [passwordIssues, setPasswordIssues] = useState([]);
    const [isRegistering, setIsRegistering] = useState(false); // New state to control registration UI
    const { signup, login } = useContext(AuthContext);

    // Validate password as user types (for registration form)
    useEffect(() => {
        if (password.length > 0 && isRegistering) { // Only validate if registering and password is being typed
            const issues = validatePasswordClientSide(password, email, fullName, confirmPassword);
            setPasswordIssues(issues);
        } else {
            setPasswordIssues([]);
        }
    }, [password, email, fullName, confirmPassword, isRegistering]); // Added confirmPassword and isRegistering to deps


    const handleAuthAction = async (actionType) => {
        setMessage('');
        setIsSuccess(false);

        if (!email || !password) {
            setMessage("Please enter both email and password.");
            return;
        }

        if (actionType === 'register') {
            if (!fullName || !phoneNumber || !department || !position) {
                setMessage("Please fill in all required user details (Full Name, Phone Number, Department, Position).");
                return;
            }
            if (password !== confirmPassword) { // Check password confirmation FIRST
                setMessage("Passwords do not match!");
                // Optionally, update passwordIssues to highlight this specific problem
                setPasswordIssues(prev => [...prev.filter(issue => issue !== "Passwords do not match."), "Passwords do not match."]);
                return;
            }

            // Client-side password policy check before calling Firebase Auth
            const clientValidationErrors = validatePasswordClientSide(password, email, fullName, confirmPassword); // Pass confirmPassword
            if (clientValidationErrors.length > 0) {
                setMessage("Password does not meet the requirements. Please see the guidelines above.");
                setPasswordIssues(clientValidationErrors);
                return;
            }
        }

        try {
            let userCredential;
            if (actionType === 'login') {
                userCredential = await login(email, password);
                setMessage("Logged in successfully!");
            } else { // 'register'
                userCredential = await signup(email, password);
                setMessage("Registered successfully!");

                const userUid = userCredential.user.uid;
                const personnelDocRef = doc(db, 'personnel', userUid);
                // ... inside handleAuthAction for 'register' case ...
                await setDoc(personnelDocRef, {
                    email: userCredential.user.email,
                    displayName: fullName,
                    createdAt: new Date(),
                    fullName: fullName,
                    phoneNumber: phoneNumber,
                    department: department,
                    position: position,
                    status: 'pending_approval',
                    roles: ['pending'] // <--- NEW: Assign 'pending' role initially
                });
                // ...
                setMessage("Registered and personnel document created! Please wait for administrator approval to access the inventory.");
            }
            setIsSuccess(true);
        } catch (error) {
            let errorMessage = error.message;
            if (error.code === 'auth/weak-password') {
                errorMessage = "The password is too weak. " + (passwordIssues.length > 0 ? "Please review the password guidelines." : "Ensure it meets complexity requirements.");
            } else if (error.code === 'auth/email-already-in-use') {
                errorMessage = "This email address is already in use. Try logging in or resetting your password.";
            } else if (error.code === 'auth/invalid-email') {
                errorMessage = "The email address is not valid.";
            } else if (error.code === 'auth/user-not-found' || error.code === 'auth/wrong-password') {
                errorMessage = "Invalid email or password.";
            } else if (error.code === 'auth/too-many-requests') {
                // --- THIS IS THE NEW PART FOR ABUSE PROTECTION ---
                errorMessage = "Too many failed login attempts. Your account has been temporarily blocked for security reasons. Please try again in a few minutes, or use the 'Forgot Password?' option if you cannot remember your password.";
            } else {
                // Default fallback for other unexpected errors
                errorMessage = `An unexpected error occurred: ${error.message}`;
            }
            // --- End of Enhanced Error Handling ---

            setMessage(`Error: ${errorMessage}`);
            setIsSuccess(false);
            console.error("Auth Error:", error);
        }
    };

    const handleForgotPassword = async () => {
        if (!email) {
            setMessage("Please enter your email address to reset your password.");
            setIsSuccess(false);
            return;
        }
        try {
            await sendPasswordResetEmail(auth, email);
            setMessage("Password reset email sent! Please check your inbox.");
            setIsSuccess(true);
        } catch (error) {
            let errorMessage = error.message;
            if (error.code === 'auth/user-not-found') {
                errorMessage = "No account found with that email address.";
            } else if (error.code === 'auth/invalid-email') {
                errorMessage = "The email address is not valid.";
            }
            setMessage(`Error sending password reset email: ${errorMessage}`);
            setIsSuccess(false);
            console.error("Forgot Password Error:", error);
        }
    };

    // Helper to determine if a requirement is met for display
    const isRequirementMet = (requirementText) => {
        if (!isRegistering) return true; // Don't show issues if not registering
        const issues = validatePasswordClientSide(password, email, fullName, confirmPassword); // Re-run validation for display
        return !issues.some(issue => issue.includes(requirementText));
    };

    return (
        <div style={{ padding: '20px', border: '1px solid #ccc', borderRadius: '8px', maxWidth: '450px', margin: '20px auto' }}>
            <h2>{isRegistering ? "Register New Account" : "Login to Property Management System"}</h2>
            <div style={formStyles}>
                <input
                    type="email"
                    placeholder="Email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    style={inputStyles}
                />
                <input
                    type="password"
                    placeholder="Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    style={inputStyles}
                />

                {/* Conditional display for Confirm Password and Password Guidelines */}
                {isRegistering && (
                    <>
                        <input
                            type="password"
                            placeholder="Confirm Password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            style={inputStyles}
                            required
                        />

                        {/* Password Guidelines */}
                        {password.length > 0 && ( // Only show guidelines if password is being typed
                            <div style={passwordGuideStyles}>
                                <strong>Password Guidelines:</strong>
                                <ul>
                                    <li style={isRequirementMet("at least 6 characters") ? passwordOkStyles : passwordIssueStyles}>
                                        At least 6 characters long
                                    </li>
                                    <li style={isRequirementMet("one uppercase letter") ? passwordOkStyles : passwordIssueStyles}>
                                        At least one uppercase letter
                                    </li>
                                    <li style={isRequirementMet("one lowercase letter") ? passwordOkStyles : passwordIssueStyles}>
                                        At least one lowercase letter
                                    </li>
                                    <li style={isRequirementMet("one number") ? passwordOkStyles : passwordIssueStyles}>
                                        At least one number
                                    </li>
                                    <li style={isRequirementMet("one special character") ? passwordOkStyles : passwordIssueStyles}>
                                        At least one special character (e.g., !@#$%^&*)
                                    </li>
                                    <li style={isRequirementMet("parts of your email or full name") ? passwordOkStyles : passwordIssueStyles}>
                                        Should not contain parts of your email or full name
                                    </li>
                                    <li style={isRequirementMet("Passwords do not match") ? passwordOkStyles : passwordIssueStyles}>
                                        Passwords match
                                    </li>
                                </ul>
                                {passwordIssues.length > 0 && <p style={passwordIssueStyles}>Please resolve the highlighted issues.</p>}
                            </div>
                        )}

                        <hr style={{ margin: '10px 0' }} />
                        <h3 style={{ marginBottom: '5px' }}>New User Details:</h3>
                        <input
                            type="text"
                            placeholder="Full Name"
                            value={fullName}
                            onChange={(e) => setFullName(e.target.value)}
                            style={inputStyles}
                            required
                        />
                        <input
                            type="tel"
                            placeholder="Phone Number"
                            value={phoneNumber}
                            onChange={(e) => setPhoneNumber(e.target.value)}
                            style={inputStyles}
                            required
                        />
                        <input
                            type="text"
                            placeholder="Department (e.g., Inventory, Procurement)"
                            value={department}
                            onChange={(e) => setDepartment(e.target.value)}
                            style={inputStyles}
                            required
                        />
                        <input
                            type="text"
                            placeholder="Position (e.g., Manager, Staff, Clerk)"
                            value={position}
                            onChange={(e) => setPosition(e.target.value)}
                            style={inputStyles}
                            required
                        />
                    </>
                )}

                <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                    {!isRegistering && ( // Show login button if not registering
                        <button onClick={() => handleAuthAction('login')} style={buttonStyles}>Log In</button>
                    )}
                    {isRegistering ? ( // Show Register button if registering
                        <button onClick={() => handleAuthAction('register')} style={successButtonStyles}>Register New Account</button>
                    ) : ( // Otherwise, show "Go to Registration" button
                        <button onClick={() => setIsRegistering(true)} style={successButtonStyles}>Go to Registration</button>
                    )}
                </div>
                {!isRegistering && ( // Only show "Forgot Password" if not registering
                    <button onClick={handleForgotPassword} style={textButtonStyles}>Forgot Password?</button>
                )}
                {isRegistering && ( // Allow user to go back to login form
                    <button onClick={() => { setIsRegistering(false); setMessage(''); setPasswordIssues([]); setConfirmPassword(''); }} style={textButtonStyles}>Back to Login</button>
                )}
            </div>
            {message && (
                <div style={isSuccess ? successMessageStyles : errorMessageStyles}>
                    {message}
                </div>
            )}
        </div>
    );
}
